import React, { useState, useCallback, useEffect } from 'react';
import { ApolloProvider, useApolloClient, useQuery } from 'react-apollo';

/**
 * ApolloProvider for useSafeQuery.
 * @param {Apollo Client.} client
 */
function SafeQueryApolloProvider(_a) {
    var children = _a.children, client = _a.client;
    return (React.createElement(ApolloProvider, { client: client }, children));
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

/**
 * Hook that works just like useQuery, but adapts onCompleted and onError to be fired whenever.
 * It always makes error and data available.
 * @param {Query to be executed.} query
 * @param {Query options.} options
 */
function useSafeQuery(query, options) {
    var apolloClient = useApolloClient();
    if (!apolloClient) {
        throw new Error('You need to declare your <SafeQueryApolloProviderProps /> and pass down your Apollo Client to use this.');
    }
    var _a = useState(false), loading = _a[0], setLoading = _a[1];
    var _b = useState(undefined), error = _b[0], setError = _b[1];
    var _c = useState(undefined), data = _c[0], setData = _c[1];
    var queryPayload = useQuery(query, __assign(__assign({}, options), { 
        // Inject Apollo Client from Context API
        client: apolloClient, 
        // Include both "errors" and "data" in query response
        errorPolicy: 'all', 
        // Skip so we can control query execution via refetch
        skip: true }));
    var handleRefetch = useCallback(function (variables) { return new Promise(function (resolve, reject) {
        if (!(queryPayload === null || queryPayload === void 0 ? void 0 : queryPayload.refetch)) {
            return;
        }
        setLoading(true);
        queryPayload.refetch(variables)
            .then(function (result) {
            var newError = result === null || result === void 0 ? void 0 : result.errors;
            var newData = result === null || result === void 0 ? void 0 : result.data;
            setError(newError);
            setData(newData);
            setLoading(false);
            if (options === null || options === void 0 ? void 0 : options.onCompleted) {
                options.onCompleted(newData);
            }
            if ((options === null || options === void 0 ? void 0 : options.onError) && newError) {
                options.onError(newError);
            }
            resolve(result);
        })
            .catch(function (queryError) {
            reject(queryError);
        });
    }); }, []);
    // Run the query manually when component mounts
    useEffect(function () {
        if (options === null || options === void 0 ? void 0 : options.skip) {
            return;
        }
        handleRefetch(options === null || options === void 0 ? void 0 : options.variables);
    }, []);
    var payload = __assign(__assign({}, queryPayload), { refetch: handleRefetch, loading: loading,
        error: error,
        data: data });
    return payload;
}

export { SafeQueryApolloProvider, useSafeQuery };
//# sourceMappingURL=index.es.js.map
